import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  FindReplace as FindReplaceIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  LocationOn as LocationOnIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { AuthContext } from '../Auth/AuthContext.jsx';

const CSVUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingByState, setDeletingByState] = useState(false);
  const [deletingFederal, setDeletingFederal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [processingDuplicates, setProcessingDuplicates] = useState(false);
  const [stateDeleteDialogOpen, setStateDeleteDialogOpen] = useState(false);
  const [selectedState, setSelectedState] = useState('');

  // List of US States
  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming', 'District of Columbia'
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      
      // Validate file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        setError('File size must be less than 100MB');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('csvFile', selectedFile);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/uploadCSV`, {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      setUploadResult(result);
    } catch (err) {
      setError(err.message || 'An error occurred during upload');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    // Reset file input
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/downloadExcel`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/csv'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'representatives.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Convert response to blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      setUploadResult({
        success: true,
        message: 'CSV file downloaded successfully!',
        summary: {
          totalRows: 0,
          successful: 0,
          created: 0,
          updated: 0,
          failed: 0
        }
      });
    } catch (err) {
      setError(err.message || 'An error occurred during download');
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteAll = async () => {
    // Show confirmation dialog
    const confirmDelete = window.confirm(
      '⚠️ WARNING: This will permanently delete ALL representatives data from the database!\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you absolutely sure you want to delete all representatives?'
    );

    if (!confirmDelete) {
      return;
    }

    // Second confirmation for safety
    const doubleConfirm = window.confirm(
      '🚨 FINAL CONFIRMATION 🚨\n\n' +
      'You are about to delete ALL representatives data.\n\n' +
      'Type YES in your mind and click OK to proceed, or Cancel to abort.'
    );

    if (!doubleConfirm) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/deleteAll`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      // Show success message
      setUploadResult({
        success: true,
        message: result.message || 'All representatives deleted successfully!',
        summary: {
          totalRows: result.deleted || 0,
          successful: result.deleted || 0,
          created: 0,
          updated: 0,
          failed: 0
        }
      });

      // Reset file selection
      handleReset();

      alert(`✅ Successfully deleted ${result.deleted} representative(s)`);
    } catch (err) {
      setError(err.message || 'An error occurred during deletion');
      alert(`❌ Error: ${err.message || 'Failed to delete representatives'}`);
    } finally {
      setDeleting(false);
    }
  };

  // Find duplicates based on name, position, area, and state
  const findDuplicates = async () => {
    setProcessingDuplicates(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/findDuplicates`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to find duplicates');
      }

      setDuplicates(result.duplicates || []);
      setDuplicatesDialogOpen(true);
    } catch (err) {
      setError(err.message || 'Error finding duplicates');
    } finally {
      setProcessingDuplicates(false);
    }
  };

  // Remove duplicates without pictures
  const removeDuplicatesWithoutPictures = async () => {
    setProcessingDuplicates(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/removeDuplicatesWithoutPictures`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to remove duplicates');
      }

      setUploadResult({
        message: result.message,
        summary: {
          totalRows: result.removed || 0,
          successful: result.removed || 0,
          created: 0,
          updated: 0,
          failed: 0
        }
      });

      // Refresh duplicates list
      findDuplicates();
    } catch (err) {
      setError(err.message || 'Error removing duplicates');
    } finally {
      setProcessingDuplicates(false);
    }
  };

  const closeDuplicatesDialog = () => {
    setDuplicatesDialogOpen(false);
  };

  // Delete representatives by state
  const handleDeleteByState = async () => {
    if (!selectedState) {
      setError('Please select a state');
      return;
    }

    const confirmDelete = window.confirm(
      `⚠️ WARNING: This will permanently delete ALL representatives from ${selectedState}!\n\n` +
      'This action cannot be undone.\n\n' +
      'Are you absolutely sure you want to proceed?'
    );

    if (!confirmDelete) {
      return;
    }

    setDeletingByState(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/deleteByState`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stateName: selectedState })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      setUploadResult({
        success: true,
        message: result.message || `Successfully deleted representatives from ${selectedState}`,
        summary: {
          totalRows: result.deleted || 0,
          successful: result.deleted || 0,
          created: 0,
          updated: 0,
          failed: 0
        }
      });

      setStateDeleteDialogOpen(false);
      setSelectedState('');
      alert(`✅ Successfully deleted ${result.deleted} representative(s) from ${selectedState}`);
    } catch (err) {
      setError(err.message || 'An error occurred during deletion');
      alert(`❌ Error: ${err.message || 'Failed to delete representatives'}`);
    } finally {
      setDeletingByState(false);
    }
  };

  // Delete federal level representatives (except President and Vice President)
  const handleDeleteFederal = async () => {
    const confirmDelete = window.confirm(
      '⚠️ WARNING: This will permanently delete ALL federal level officials!\n\n' +
      '🛡️ PROTECTED: President and Vice President will NOT be deleted.\n\n' +
      'This will delete:\n' +
      '• U.S. Senators\n' +
      '• U.S. Representatives (Congressmen/Congresswomen)\n' +
      '• Cabinet Secretaries\n' +
      '• Federal Judges\n' +
      '• Ambassadors\n' +
      '• Other federal officials\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you absolutely sure you want to proceed?'
    );

    if (!confirmDelete) {
      return;
    }

    const doubleConfirm = window.confirm(
      '🚨 FINAL CONFIRMATION 🚨\n\n' +
      'You are about to delete ALL federal representatives\n' +
      '(except President and Vice President).\n\n' +
      'Click OK to proceed, or Cancel to abort.'
    );

    if (!doubleConfirm) {
      return;
    }

    setDeletingFederal(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/reps/deleteFederal`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      setUploadResult({
        success: true,
        message: result.message || 'Successfully deleted federal representatives',
        summary: {
          totalRows: result.deleted || 0,
          successful: result.deleted || 0,
          created: 0,
          updated: 0,
          failed: 0
        }
      });

      alert(`✅ Successfully deleted ${result.deleted} federal representative(s)\n(President and Vice President preserved)`);
    } catch (err) {
      setError(err.message || 'An error occurred during deletion');
      alert(`❌ Error: ${err.message || 'Failed to delete federal representatives'}`);
    } finally {
      setDeletingFederal(false);
    }
  };

  
  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return 'success';
      case 'updated': return 'info';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Representative CSV Upload
      </Typography>

      {/* File Upload Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload & Download CSV File
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Upload CSV files to bulk import representatives data, download all existing representatives data in CSV format, or delete all representatives data from the database.
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <label htmlFor="csv-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              sx={{ mr: 2 }}
            >
              Choose CSV File
            </Button>
          </label>
          
          {selectedFile && (
            <Chip
              icon={<DescriptionIcon />}
              label={`${selectedFile.name} (${(selectedFile.size / 1024).toFixed(1)} KB)`}
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {selectedFile && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={uploading}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={handleDownload}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {downloading ? 'Downloading...' : 'Download All Representatives'}
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAll}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete All Representatives'}
          </Button>
        </Box>

        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Processing CSV file...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Advanced Delete Options Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: 'error.50' }}>
        <Typography variant="h6" gutterBottom color="error">
          Advanced Delete Options
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Delete representatives by specific criteria. Use with caution - these actions cannot be undone.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Delete by State Button */}
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setStateDeleteDialogOpen(true)}
            disabled={deletingByState}
            startIcon={deletingByState ? <CircularProgress size={20} /> : <LocationOnIcon />}
          >
            {deletingByState ? 'Deleting...' : 'Delete by State'}
          </Button>

          {/* Delete Federal Officials Button */}
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteFederal}
            disabled={deletingFederal}
            startIcon={deletingFederal ? <CircularProgress size={20} /> : <AccountBalanceIcon />}
          >
            {deletingFederal ? 'Deleting...' : 'Delete Federal Officials (except President/VP)'}
          </Button>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Delete by State:</strong> Removes all representatives from a selected state.<br />
            <strong>Delete Federal Officials:</strong> Removes all federal level officials except President and Vice President (includes Senators, Representatives, Cabinet members, etc.).
          </Typography>
        </Alert>
      </Paper>

      {/* Delete by State Dialog */}
      <Dialog 
        open={stateDeleteDialogOpen} 
        onClose={() => setStateDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" color="error">
              Delete Representatives by State
            </Typography>
            <IconButton onClick={() => setStateDeleteDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This action will permanently delete ALL representatives from the selected state. This cannot be undone.
          </Alert>
          
          <FormControl fullWidth>
            <InputLabel id="state-select-label">Select State</InputLabel>
            <Select
              labelId="state-select-label"
              value={selectedState}
              label="Select State"
              onChange={(e) => setSelectedState(e.target.value)}
            >
              {usStates.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStateDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteByState} 
            color="error" 
            variant="contained"
            disabled={!selectedState || deletingByState}
            startIcon={deletingByState ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deletingByState ? 'Deleting...' : `Delete All from ${selectedState || 'State'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Management Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Duplicate Management
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Find and manage duplicate representatives based on name, position, area, and state.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={findDuplicates}
            disabled={processingDuplicates}
            startIcon={processingDuplicates ? <CircularProgress size={20} /> : <FindReplaceIcon />}
            color="info"
          >
            {processingDuplicates ? 'Finding...' : 'Show Duplicates'}
          </Button>

          <Button
            variant="outlined"
            onClick={removeDuplicatesWithoutPictures}
            disabled={processingDuplicates}
            startIcon={processingDuplicates ? <CircularProgress size={20} /> : <DeleteIcon />}
            color="warning"
          >
            {processingDuplicates ? 'Removing...' : 'Remove Duplicates Without Pictures'}
          </Button>
        </Box>
      </Paper>

      {/* Duplicates Dialog */}
      <Dialog 
        open={duplicatesDialogOpen} 
        onClose={closeDuplicatesDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Duplicate Representatives ({duplicates.length} groups)
            </Typography>
            <IconButton onClick={closeDuplicatesDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {duplicates.length === 0 ? (
            <Alert severity="success">
              No duplicates found! All representatives are unique.
            </Alert>
          ) : (
            duplicates.map((group, groupIndex) => (
              <Accordion key={groupIndex} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {group.name} - {group.current_elected_position}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {group.administrative_area}, {group.state} ({group.count} duplicates)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Position</TableCell>
                          <TableCell>Area</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Has Picture</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.representatives.map((rep, repIndex) => (
                          <TableRow 
                            key={repIndex}
                            sx={{ 
                              bgcolor: !rep.official_image_url || rep.official_image_url === 'N/A' 
                                ? 'error.lighter' 
                                : 'inherit' 
                            }}
                          >
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {rep._id}
                            </TableCell>
                            <TableCell>{rep.name}</TableCell>
                            <TableCell>{rep.current_elected_position}</TableCell>
                            <TableCell>{rep.administrative_area}</TableCell>
                            <TableCell>{rep.state}</TableCell>
                            <TableCell>{rep.official_email_id || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip 
                                label={rep.official_image_url && rep.official_image_url !== 'N/A' ? 'Yes' : 'No'}
                                color={rep.official_image_url && rep.official_image_url !== 'N/A' ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDuplicatesDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload Result Display */}
      {uploadResult && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Upload Results
          </Typography>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: '14px' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    Total Rows
                  </Typography>
                  <Typography variant="h4">
                    {uploadResult.summary?.totalRows || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: '14px' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="success.main" gutterBottom>
                    Successful
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {uploadResult.summary?.successful || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: '14px' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="info.main" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {uploadResult.summary?.created || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: '14px' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="warning.main" gutterBottom>
                    Updated
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {uploadResult.summary?.updated || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: '14px' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography color="error.main" gutterBottom>
                    Failed
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {uploadResult.summary?.failed || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Success Message */}
          <Alert severity="success" sx={{ mb: 3 }}>
            {uploadResult.message}
          </Alert>

          {/* Additional Information */}
          {(uploadResult.resultsCount > 0 || uploadResult.errorsCount > 0) && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {uploadResult.resultsCount > 0 && (
                <Grid item xs={12} sm={6}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Results Available:</strong> {uploadResult.resultsCount} detailed records processed
                    </Typography>
                  </Alert>
                </Grid>
              )}
              {uploadResult.errorsCount > 0 && (
                <Grid item xs={12} sm={6}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Errors Encountered:</strong> {uploadResult.errorsCount} records had issues
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}

          {/* Detailed Results - if provided by backend */}
          {uploadResult.results && uploadResult.results.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Processed Records ({uploadResult.results.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Area</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadResult.results.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell>{result.row}</TableCell>
                          <TableCell>{result.name}</TableCell>
                          <TableCell>{result.position}</TableCell>
                          <TableCell>{result.area}</TableCell>
                          <TableCell>
                            <Chip
                              label={result.status}
                              color={getStatusColor(result.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {result.id}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Error Details - if provided by backend */}
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" color="error">
                  Errors ({uploadResult.errors.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {uploadResult.errors.map((error, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={`Row ${error.row}: ${error.error}`}
                        secondary={
                          <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem', mt: 1 }}>
                            {JSON.stringify(error.data, null, 2)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Paper>
      )}

      {/* CSV Format Requirements */}
      <Paper elevation={1} sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          CSV Format Requirements
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Required Columns:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="first_name" secondary="Representative's first name" />
              </ListItem>
              <ListItem>
                <ListItemText primary="last_name" secondary="Representative's last name" />
              </ListItem>
              <ListItem>
                <ListItemText primary="current_elected_position" secondary="Current position held" />
              </ListItem>
              <ListItem>
                <ListItemText primary="administrative_area" secondary="Area they represent" />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Optional Columns:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="official_email_id" secondary="Official email address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="state" secondary="State/Province" />
              </ListItem>
              <ListItem>
                <ListItemText primary="official_image_url" secondary="Profile image URL" />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="textSecondary">
          • File must be in .csv format<br />
          • Maximum file size: 10MB<br />
          • Column headers must match exactly (case-sensitive)<br />
          • Empty optional fields will default to 'N/A'
        </Typography>
      </Paper>
    </Box>
  );
};

export default CSVUpload;
